import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Fade,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from "@mui/material";
import {useEffect, useLayoutEffect, useRef, useState} from "react";
import {keepPreviousData, useQuery} from '@tanstack/react-query';
import {useLocation, useSearchParams} from "react-router";
import GenericError from "src/components/GenericError";
import Scrollbar from "src/components/Scrollbar";
import CustomizableListSearch from "./CustomizableListSearch";
import CustomizableListTableBody from "./CustomizableListTableBody";
import CustomizableListTableFooter from "./CustomizableListTableFooter";
import CustomizableListTableHeader from "./CustomizableListTableHeader";
import encodeFilterGroups from "../complex-filter/encodeFilterGroups";
import EmptyState from '../../common/EmptyState';

// URL keys used by `persistStateInUrl`. Kept distinct from page-level seed
// params (`connection_age`, `status`, …) so both can coexist in one URL.
const URL_STATE_KEYS = ['q', 'filters', 'adv', 'advf', 'ordering', 'page', 'page_size'];

// Decode the compact `[[{c,o,v,n}]]` encoding — the same string
// ComplexFilterContextProvider emits and the backend consumes — back into the
// panel's filterGroups shape so a refreshed page can rebuild its filter rows.
// Rows are validated against `filterHeaders`: a persisted URL can outlive a
// column rename (or be hand-edited), and an unknown column would crash
// ComplexFilterRow's header lookup during render.
const decodePersistedFilterGroups = (raw, filterHeaders) => {
  if (!raw) return null;
  let groups;
  try {
    groups = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(groups)) return null;
  const headersByColumn = new Map((filterHeaders || []).map((h) => [h.machineName, h]));
  const out = {};
  let groupId = 0;
  groups.forEach((groupFilters) => {
    if (!Array.isArray(groupFilters)) return;
    const filters = {};
    let filterId = 0;
    groupFilters.forEach((f) => {
      if (!f || typeof f !== 'object' || f.c === undefined || f.o === undefined) return;
      const header = headersByColumn.get(f.c);
      if (!header) return;
      if (!header.operators?.some((op) => op.machineName === f.o) && f.o !== 'isnull') return;
      filterId += 1;
      filters[filterId] = {
        filterId,
        active: true,
        column: f.c,
        operator: f.o,
        value: f.v,
        type: f.n === 0,
      };
    });
    if (filterId === 0) return;
    groupId += 1;
    out[groupId] = { groupId, filters };
  });
  return groupId > 0 ? out : null;
};

// The provider emits `[[]]` for an empty panel — that's "no filters", not
// state worth persisting. No header validation here: the string being tested
// comes from the live panel, not from the URL.
const hasAnyEncodedFilter = (raw) => {
  if (!raw) return false;
  try {
    const groups = JSON.parse(raw);
    return Array.isArray(groups)
      && groups.some((g) => Array.isArray(g) && g.some((f) => f && typeof f === 'object'));
  } catch {
    return false;
  }
};

const parseJsonObjectParam = (raw) => {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    return value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0
      ? value
      : null;
  } catch {
    return null;
  }
};

const parsePositiveIntParam = (raw, max) => {
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 && value <= max ? value : null;
};

// Reserved group id for `pinnedFilterGroup` — far above anything the panel's
// own group counter produces, so caller-pinned rows never collide with the
// user's groups.
export const PINNED_FILTER_GROUP_ID = 9001;

const CustomizableListBase = ({
                                queryParams,
                                setQueryParams,
                                tableHeaders,
                                filterHeaders,
                                queryDef,
                                customRowRenderer,
                                renderResultsToolbar,
                                compactRowRenderer,
                                compactBreakpoint = 1100,
                                tableSx,
                                defaultOrdering,
                                defaultPageSize,
                                pageResetKey,
                                disableOrdering,
                                disablePagination,
                                disableSearch,
                                disableComplexFilter,
                                advancedSearch,
                                tableMaxHeight,
                                filterGroups,
                                setFilterGroups,
                                searchParams,
                                setSearchParams,
                                setResultCount,
                                extraQueryMethod,
                                fixedQueryParams,
                                pinnedFilterGroup,
                                emptyState,
                                pageKey,
                                exportUrl,
                                restoredState,
                                persistStateInUrl,
                                setUrlSearchParams,
                              }) => {
  const [isInited, setIsInited] = useState(false);
  const layoutRef = useRef(null);
  const [compact, setCompact] = useState(false);
  const responsive = Boolean(compactRowRenderer);
  useLayoutEffect(() => {
    if (!responsive || !layoutRef.current) return undefined;
    const update = (width) => { if (width > 0) setCompact(width < compactBreakpoint); };
    update(layoutRef.current.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => update(entry.contentRect.width));
    observer.observe(layoutRef.current);
    return () => observer.disconnect();
  }, [responsive, compactBreakpoint]);
  const location = useLocation();
  const [ordering, setOrdering] = useState(restoredState?.ordering ?? defaultOrdering ?? '');
  const [page, setPage] = useState(restoredState?.page ?? 0);
  const previousPageResetKey = useRef(pageResetKey);
  useEffect(() => {
    if (previousPageResetKey.current === pageResetKey) return;
    previousPageResetKey.current = pageResetKey;
    setPage(0);
  }, [pageResetKey]);
  // Which page (if any) was restored from the URL. Cleared on the first
  // successful fetch; a stale-page 404 on a restored page jumps straight to
  // page 0 instead of stepping back one page per failed request — a
  // bookmarked ?page=40 over a shrunken result set must not fire 37
  // sequential 404s.
  const restoredPageRef = useRef(restoredState?.page ?? null);
  // Callers like the assignment wizard render the table inside a dialog or
  // a tall multi-step form, where 10 rows + filter header + nav bars don't
  // fit on a typical viewport. They pass defaultPageSize=5 to keep the
  // whole step visible without scrolling.
  const [pageSize, setPageSize] = useState(restoredState?.pageSize ?? defaultPageSize ?? 10);
  // Seeding `filterParams` from the restored raw string (instead of waiting
  // for CustomizableListSearch's mount effect to re-encode the groups) puts
  // the filters on the very first fetch, avoiding a flash of unfiltered rows.
  const [filterParams, setFilterParams] = useState(restoredState?.filterParamsRaw ?? '');
  const [bodyParams, setBodyParams] = useState(restoredState?.bodyParams ?? '');
  // Raw form values of the last applied advanced search (devices page).
  // Persisted alongside `bodyParams` so a refresh can repopulate the form,
  // not just re-run the query.
  const [advancedFormValues, setAdvancedFormValues] = useState(restoredState?.advancedFormValues ?? null);

  // Caller-fixed query params (e.g. a page-level quick filter such as the
  // parameter type chips). Compared by content, not identity, so an inline
  // object literal doesn't retrigger the query build; a content change
  // resets pagination because the result set is a different slice.
  const fixedQueryKey = JSON.stringify(fixedQueryParams ?? null);
  const fixedQueryRef = useRef(fixedQueryKey);
  useEffect(() => {
    if (fixedQueryRef.current === fixedQueryKey) return;
    fixedQueryRef.current = fixedQueryKey;
    setPage(0);
  }, [fixedQueryKey]);

  // A caller-controlled filter group under the reserved id, kept visible in
  // the filter panel. Content changes merge over the current groups and apply
  // immediately (the saved-preset pattern), leaving the user's own groups
  // alone. The mount state comes from `initialFilterGroups`, so the first
  // content snapshot only arms the comparison.
  const pinnedKey = JSON.stringify(pinnedFilterGroup ?? null);
  const pinnedRef = useRef();
  // Remount counter for the filter panel's provider: it copies the groups
  // once on mount (the saved-preset pattern bumps a version to re-seed), so
  // a pinned change has to bump it too or the visible rows go stale.
  const [pinnedVersion, setPinnedVersion] = useState(0);
  useEffect(() => {
    if (pinnedRef.current === undefined) {
      pinnedRef.current = pinnedKey;
      return;
    }
    if (pinnedRef.current === pinnedKey) return;
    pinnedRef.current = pinnedKey;
    const { [PINNED_FILTER_GROUP_ID]: _previous, ...rest } = filterGroups || {};
    const next = pinnedFilterGroup ? { ...rest, [PINNED_FILTER_GROUP_ID]: pinnedFilterGroup } : rest;
    setFilterGroups(next);
    setFilterParams(encodeFilterGroups(next));
    setPage(0);
    setPinnedVersion((version) => version + 1);
  }, [pinnedKey]);

  // Variable for toggling the search and filter panel.
  const [openCollapse, setOpenCollapse] = useState(false);

  const [toggleSearch, setToggleSearch] = useState(false);

  const skeletonCount = 5;
  const SkeletonRenderer = () => (
    <TableBody>
      {[...Array(skeletonCount).keys()].map(item => (
        <Fade key={`skeleton-${item}`} in={true} timeout={300} unmountOnExit={true}>
          <TableRow>
            {tableHeaders.map((tc => (
              <TableCell
                key={`skeleton-${item}-cell-${tc.machineName}`}
              >
                <Box sx={{my: 0.25, width: "80%"}}>
                  <Skeleton height={18} animation="wave" variant="text"/>
                </Box>
              </TableCell>
            )))}
          </TableRow>
        </Fade>
      ))}
    </TableBody>
  )

  useEffect(() => {
    // Always rebuild the query string from current state — including on the
    // very first render. The previous version skipped the build on mount
    // (just flipped `isInited`), which meant the initial fetch went out
    // with whatever the parent's queryParams happened to be (usually empty)
    // and `defaultPageSize` / `defaultOrdering` were silently dropped on
    // the first round-trip. The pagination footer would show "5" because
    // local state was correct, but the table still showed 10 rows because
    // the server got no page_size hint.
    const urlQueryParams = new URLSearchParams();

    if (!disableOrdering && ordering) {
      urlQueryParams.append('ordering', ordering);
    }

    if (!disablePagination && page) {
      // Table pagination is 0-indexed
      // but Django is 1-indexed, hence page+1
      urlQueryParams.append('page', page + 1);
    }

    if (!disablePagination && pageSize) {
      urlQueryParams.append('page_size', pageSize);
    }

    if (!disableSearch && searchParams) {
      urlQueryParams.append('search', searchParams);
    }

    if (!disableComplexFilter && filterParams) {
      // TODO Change this to 'complex_filter'
      urlQueryParams.append('complex-filter', filterParams);
      if (extraQueryMethod){
          urlQueryParams.append('extra-query-method', extraQueryMethod);
      }
    }

    if (advancedSearch && bodyParams) {
      urlQueryParams.append('advanced_filter', JSON.stringify(bodyParams));
    }

    if (fixedQueryParams) {
      Object.entries(fixedQueryParams).forEach(([name, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          urlQueryParams.append(name, value);
        }
      });
    }

    setQueryParams(urlQueryParams.toString());
    if (!isInited) setIsInited(true);
  }, [ordering, page, pageSize, searchParams, filterParams, bodyParams, fixedQueryKey]);

  // Mirror the applied list state into the URL (replace — no history spam) so
  // a refresh reconstructs the exact view. Only non-default parts are written;
  // params owned by others (page-level seeds like `connection_age`) are kept.
  // The base is read from window.location rather than the useSearchParams
  // tuple to avoid a reactive dependency on our own writes (and because this
  // router version's setter takes a plain value, not an updater).
  //
  // `location.search` is a dependency on purpose: a same-route navigation
  // that drops the params (sidebar link to the bare path, back/forward)
  // doesn't remount the list, so without it URL and view silently diverge
  // until the next interaction. Re-running on external URL changes re-asserts
  // the state; our own write makes the next run compute an identical string,
  // so it terminates. The mount run is skipped: this router version's
  // navigate() is inert until its own (parent) effect arms it, and the URL
  // the user arrived with is the baseline anyway.
  const mirrorArmedRef = useRef(false);
  useEffect(() => {
    if (!persistStateInUrl) return;
    if (!mirrorArmedRef.current) {
      mirrorArmedRef.current = true;
      return;
    }
    const next = new URLSearchParams(window.location.search);
    const setOrDelete = (key, value) => {
      if (value) next.set(key, value);
      else next.delete(key);
    };
    setOrDelete('q', searchParams || '');
    setOrDelete('filters', hasAnyEncodedFilter(filterParams) ? filterParams : '');
    // `advf` (raw form values) only makes sense alongside an active advanced
    // query — the panel's Apply clears bodyParams, and the stale form values
    // must not outlive it in the URL. Oversized payloads (multiline paste
    // lists in fiscal_id/ips) are not persisted at all: a query string past
    // ~4KB gets rejected by common proxy header limits, which would turn the
    // refresh this feature exists for into a hard error page.
    const advancedActive = bodyParams && Object.keys(bodyParams).length > 0;
    const advJson = advancedActive ? JSON.stringify(bodyParams) : '';
    const advfJson = advancedActive && advancedFormValues ? JSON.stringify(advancedFormValues) : '';
    const advFits = advJson && encodeURIComponent(advJson).length <= 3000;
    const advfFits = advFits && advfJson
      && encodeURIComponent(advJson).length + encodeURIComponent(advfJson).length <= 3500;
    setOrDelete('adv', advFits ? advJson : '');
    setOrDelete('advf', advfFits ? advfJson : '');
    setOrDelete('ordering', ordering !== (defaultOrdering ?? '') ? ordering : '');
    setOrDelete('page', page > 0 ? String(page + 1) : '');
    setOrDelete('page_size', pageSize !== (defaultPageSize ?? 10) ? String(pageSize) : '');
    if (next.toString() !== window.location.search.replace(/^\?/, '')) {
      setUrlSearchParams(next, { replace: true });
    }
  }, [ordering, page, pageSize, searchParams, filterParams, bodyParams, advancedFormValues,
      persistStateInUrl, location.search]);

  // Hold the fetch until the first effect run has pushed the initial
  // defaults into queryParams; otherwise we'd send an empty-param request
  // first and then immediately refetch with the right params, briefly
  // flashing the unintended 10-row default to the user.
  const queryKey = Array.isArray(queryDef.key) ? [...queryDef.key, queryParams, bodyParams] : [queryDef.key, queryParams, bodyParams];
  const query = useQuery({
    queryKey,
    queryFn: () => queryDef.func(queryParams, bodyParams),
    enabled: isInited,
    placeholderData: responsive ? keepPreviousData : undefined
  });

  // A shrinking result set can strand us on a now-out-of-range page: e.g. the
  // last row on the last page is deleted / uninstalled / unassigned, the list
  // query is invalidated, and it refetches the SAME page — which no longer
  // exists. DRF's paginator answers that with a 404 "Invalid page." Rather than
  // dumping the user on a full-page <GenericError>, step back one page and
  // refetch. Living here means every paginated list (uninstall, bulk delete,
  // revoke-access, unassign-tag, …) is covered by a single fix.
  //
  // The predicate is derived from `queryParams` — which page the FAILED request
  // actually asked for — NOT from the `page` state. `page` and `queryParams`
  // update on separate commits, so keying on `page` would flip the guard for
  // one commit and flash the very error we're suppressing. `queryParams` moves
  // in lockstep with the query's identity, so the guard stays consistent with
  // the error it belongs to. Requiring the exact "Invalid page." message keeps
  // genuine object / parent 404s (which surface on page 0 anyway) visible.
  const requestedPage = Number(new URLSearchParams(queryParams).get('page'));
  const isStalePageError =
    query.isError &&
    requestedPage > 1 &&
    query.error?.response?.status === 404 &&
    query.error?.response?.data?.errors?.[0]?.message === 'Invalid page.';

  useEffect(() => {
    // `isSuccess` alone is true for a warm cache hit before the server has
    // confirmed the restored page still exists — wait for the revalidation
    // to finish so a stale cached page still takes the jump-to-0 path below.
    if (query.isSuccess && !query.isFetching) {
      restoredPageRef.current = null;
    }
  }, [query.isSuccess, query.isFetching]);

  useEffect(() => {
    if (isStalePageError) {
      if (restoredPageRef.current !== null) {
        // The invalid page came from the URL restore, where the drift can be
        // arbitrary — jump home instead of walking back one 404 at a time.
        restoredPageRef.current = null;
        setPage(0);
        return;
      }
      // One step at a time: a bulk delete can drop several pages, so this may
      // repeat until a valid page is reached. Page 0 omits the `page` param and
      // DRF's allow_empty_first_page returns 200 even when empty, so it always
      // terminates.
      setPage((prev) => Math.max(0, prev - 1));
    }
  }, [isStalePageError]);

  // TODO Work on error handling and loading behavior
  if (query.isError && !isStalePageError) {
    console.error(query.error);
    return <GenericError error={query.error}/>;
  }

  const ListContainer = responsive ? Box : Scrollbar;
  const loaded = query.data && !isStalePageError;
  const footerProps = { count: query.data?.count ?? 0, columnCount: tableHeaders.length, setResultCount, page, setPage, pageSize, setPageSize, disablePagination };
  return (
    <ListContainer ref={layoutRef} sx={responsive ? { minWidth: 0 } : undefined}>
      <CustomizableListSearch
        responsive={responsive}
        filterGroups={filterGroups}
        setFilterGroups={setFilterGroups}
        disableSearch={disableSearch}
        disableComplexFilter={disableComplexFilter}
        advancedSearch={advancedSearch}
        toggleSearch={toggleSearch}
        setToggleSearch={setToggleSearch}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        setBodyParams={setBodyParams}
        setFilterParams={setFilterParams}
        filterHeaders={filterHeaders}
        openCollapse={openCollapse}
        setOpenCollapse={setOpenCollapse}
        setPage={setPage} // To be able to reset page number on a search.
        bodyParams={bodyParams}
        queryParams={queryParams}
        pageKey={pageKey}
        exportUrl={exportUrl}
        advancedFormValues={advancedFormValues}
        setAdvancedFormValues={setAdvancedFormValues}
        pinnedVersion={pinnedVersion}
      />
      {
        (disableSearch || !filterHeaders || advancedSearch) && <Divider/>
      }
      {/* Give bulk controls the current query's rows and readiness together,
          so cached counts cannot enable an action while results are loading. */}
      {renderResultsToolbar?.({
        results: loaded ? query.data.results : [],
        count: loaded ? query.data.count : 0,
        isReady: isInited && query.isSuccess && !query.isFetching && !query.isPreviousData && !isStalePageError,
      })}
      {compact && responsive ? (
        <>
          {!disableOrdering && (
            <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
              <TextField select fullWidth size="small" label="Sort by" SelectProps={{ native: true }}
                inputProps={{ 'aria-label': `Sort ${pageKey || 'records'}` }} value={ordering || ''}
                onChange={(event) => { setOrdering(event.target.value); setPage(0); }}>
                <option value="">Default order</option>
                {tableHeaders.filter((header) => header.sortable).flatMap(({ machineName, humanName }) => [
                  <option key={machineName} value={machineName}>{humanName} · ascending</option>,
                  <option key={`-${machineName}`} value={`-${machineName}`}>{humanName} · descending</option>,
                ])}
              </TextField>
            </Box>
          )}
          <Box sx={{ maxHeight: tableMaxHeight, overflowY: 'auto', minWidth: 0 }}>
            {loaded ? (
              query.data.results?.length ? query.data.results.map((item, index) => (
                <Box key={item.id ?? index}>{compactRowRenderer(item)}</Box>
              )) : (emptyState || <EmptyState title="No records to show" description="There are no items matching the current view." />)
            ) : (
              <Box sx={{ px: 2, py: 1 }} aria-label="Loading records">
                {[0, 1, 2].map((key) => <Skeleton key={key} variant="rectangular" height={160} sx={{ mb: 1.5, borderRadius: 1 }} />)}
              </Box>
            )}
          </Box>
          <CustomizableListTableFooter {...footerProps} standalone />
        </>
      ) : <TableContainer
        sx={{
          maxHeight: tableMaxHeight
        }}
      >
        <Table
          size="small"
          sx={{
            height: "max-content",
            overflow: 'hidden',
            // size="small" sets cell padding to 6px/16px. Tighten the
            // horizontal padding too so wide-column tables (Device,
            // Incident) breathe but stay compact vertically.
            '& .MuiTableCell-root': { py: 0.75 },
            ...tableSx,
          }}
        >
          <CustomizableListTableHeader
            tableHeaders={tableHeaders}
            ordering={ordering}
            setOrdering={setOrdering}
          />
          {
            // Gate on `data` rather than `isLoading`: when the query is
            // disabled (`enabled: false` between mount and first effect
            // push), react-query reports isLoading=false but data is still
            // undefined, which used to crash `results={query.data.results}`.
            // Showing the skeleton until data lands handles all three
            // states cleanly — disabled, loading, refetching.
            //
            // Also skeleton while recovering from a stale-page 404: react-query
            // retains the pre-delete page's data on the errored refetch, so
            // without this guard the just-deleted row would flash for a frame
            // before the step-back refetch lands.
            query.data && !isStalePageError ?
              (
                <CustomizableListTableBody
                  results={query.data.results}
                  rowRenderer={customRowRenderer}
                  emptyState={emptyState}
                  columnCount={tableHeaders.length}
                />
              ) : (
                <SkeletonRenderer/>
              )
          }
          <CustomizableListTableFooter {...footerProps} />
        </Table>
      </TableContainer>}
    </ListContainer>
  );
};

const CustomizableList = (props) => {
  const [queryParams, setQueryParams] = useState('');
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  // With `persistStateInUrl`, snapshot the URL-persisted list state once on
  // mount. Later URL updates come from our own mirror effect and must not
  // re-seed state. `initialFilterGroups` (page-level seeds, saved presets fed
  // through remounts) wins over the persisted filters: an explicit new intent
  // beats the previous view's state. Only enable this on one list per route —
  // two persisting lists would fight over the same URL keys.
  const restoredRef = useRef();
  if (restoredRef.current === undefined) {
    if (props.persistStateInUrl) {
      const filterGroups = props.initialFilterGroups
        ? null
        : decodePersistedFilterGroups(urlSearchParams.get('filters'), props.filterHeaders);
      const pageSize = parsePositiveIntParam(urlSearchParams.get('page_size'), 100);
      const pageSizeOptions = [5, 10, 20, 50, props.defaultPageSize ?? 10];
      restoredRef.current = {
        search: urlSearchParams.get('q') || null,
        filterGroups,
        // The canonical re-encoding (not the raw param) — identical to what
        // the panel's own mount effect will re-encode, so seeding it here
        // puts the filters on the first fetch without a divergent refetch.
        filterParamsRaw: filterGroups ? encodeFilterGroups(filterGroups) : null,
        bodyParams: parseJsonObjectParam(urlSearchParams.get('adv')),
        advancedFormValues: parseJsonObjectParam(urlSearchParams.get('advf')),
        ordering: urlSearchParams.get('ordering') || null,
        page: (parsePositiveIntParam(urlSearchParams.get('page'), 9999) ?? 1) - 1 || null,
        pageSize: pageSizeOptions.includes(pageSize) ? pageSize : null,
      };
    } else {
      restoredRef.current = null;
    }
  }
  const restored = restoredRef.current;

  // Filter Group histories. Seeded from `initialFilterGroups` so deep-link /
  // chart drill-down can pre-apply a filter without the user opening the panel.
  const [filterGroupHistory, setFilterGroupHistory] = useState(
    props.initialFilterGroups || restored?.filterGroups || {}
  );
  // Search Param histories
  const [searchHistory, setSearchHistory] = useState(restored?.search ?? null);

  // Report every applied filter-group change (panel Apply, presets, pinned
  // merges) so a page owning a quick filter can mirror the panel's state.
  // The callback is read through a ref: a parent re-render hands down a new
  // function identity without a state change, and firing on that would
  // report the pre-merge (stale) groups and echo them back into the page.
  const onFilterGroupsChangeRef = useRef(props.onFilterGroupsChange);
  onFilterGroupsChangeRef.current = props.onFilterGroupsChange;
  useEffect(() => {
    onFilterGroupsChangeRef.current?.(filterGroupHistory);
  }, [filterGroupHistory]);

  return (
    <Card sx={{mt: 2, minWidth: 0}}>
      {
        props.title ? (
          <>
            <CardHeader title={props.title} sx={{ py: 1.5 }}/>
            <Divider/>
          </>
        ) : null
      }
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <CustomizableListBase
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          filterGroups={filterGroupHistory}
          setFilterGroups={setFilterGroupHistory}
          searchParams={searchHistory}
          setSearchParams={setSearchHistory}
          restoredState={restored}
          setUrlSearchParams={setUrlSearchParams}
          {...props}
        />
      </CardContent>
    </Card>
  );
};

export {
  CustomizableList as default,
  CustomizableListBase,
  URL_STATE_KEYS,
};
